import { observer } from 'mobx-react-lite';
import React, { ReactNode } from 'react';
import { Box, Card, CardContent, CardHeader, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from '@shared/utils/i18n';
import { queryClient } from '@shared/api/reactQuery';
import { notifications } from '@shared/utils/notifications';
import { usePutApiServiceGroupSetParamPrintDetails } from '@/shared/api/orval/service-group/service-group';
import { GetApiOsiIdV2ServicesQueryResult, getGetApiOsiIdV2ServicesQueryKey } from '@/shared/api/orval/osi/osi';

interface Props {
  title: string;
  RightTitleSlot?: ReactNode;
  children: ReactNode;
  printDetailsOnInvoice: boolean;
  osiId: number;
  groupId: number;
}

export const ServiceGroup: React.FC<Props> = observer(
  ({ title, RightTitleSlot, children, printDetailsOnInvoice, osiId, groupId }) => {
    const { t } = useTranslation();

    const { mutate } = usePutApiServiceGroupSetParamPrintDetails({
      mutation: {
        onMutate: async ({ params }) => {
          const queryKey = getGetApiOsiIdV2ServicesQueryKey(params.osiId);

          await queryClient.cancelQueries({ queryKey });

          const prevState = queryClient.getQueryData(queryKey);

          queryClient.setQueryData(queryKey, (prev: GetApiOsiIdV2ServicesQueryResult) => {
            const serviceGroups = prev?.result ?? [];

            const updated = serviceGroups.map((group) => {
              if (group?.id === params.serviceGroupId) {
                return { ...group, printDetailOnInvoice: params.value };
              }
              return group;
            });
            return { result: updated };
          });

          return { prevState };
        },
        onError: (error, { params }, context) => {
          const queryKey = getGetApiOsiIdV2ServicesQueryKey(params.osiId);
          notifications.error(error.message);
          queryClient.setQueryData(queryKey, context?.prevState);
        },
        onSettled: (data, error, { params }, context) => {
          void queryClient.invalidateQueries({ queryKey: getGetApiOsiIdV2ServicesQueryKey(params.osiId) });
        }
      }
    });

    const onChange = (e: any) => {
      mutate({ params: { osiId, value: e.target.checked, serviceGroupId: groupId } });
    };

    return (
      <Card sx={{ mt: 2, maxWidth: 1000, minWidth: 800 }}>
        <CardHeader
          subheader={
            <Box className={'flex flex-col'}>
              {/* gap:2 — постоянный зазор между заголовком и кнопками (ml:auto на счёте его не даёт при нехватке места) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* minWidth:0 + flex:1 — заголовок сжимается и уступает место кнопкам справа (по умолчанию flex-элемент не сжимается ниже content-size);
                    overflowWrap/wordBreak — переносят сплошное длинное слово без пробелов по символам, чтобы оно не выдавливало кнопки за границы карточки */}
                <Typography
                  variant={'h5'}
                  color={'textPrimary'}
                  sx={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                  {title}
                </Typography>
                {RightTitleSlot}
              </Box>
              <Box className={'flex'}>
                <FormControlLabel
                  control={<Checkbox size={'small'} checked={printDetailsOnInvoice} onChange={onChange} />}
                  label={t('accruals:printDetailsOnInvoice')}
                />
              </Box>
            </Box>
          }
        />
        <CardContent sx={{ '&.MuiCardContent-root': { paddingBlockStart: '8px' } }}>{children}</CardContent>
      </Card>
    );
  }
);
